import React from 'react';
import { useAdminPermissions } from '../../hooks/useAdminPermissions.js';
import { useTheme } from '../../contexts/ThemeContext.jsx';

/**
 * AdminOverview component - Main admin dashboard overview page
 */
const AdminOverview = () => {
  const { permissions, hasPermission } = useAdminPermissions();
  const { theme, isDark, isLight } = useTheme();

  const dashboardCards = [
    {
      title: 'Users',
      description: 'Manage user accounts and subscriptions',
      icon: '👥',
      href: '/admin/users',
      permission: 'user.read',
      color: 'bg-blue-600'
    },
    {
      title: 'Parrots',
      description: 'Manage AI parrot configurations',
      icon: '🦜',
      href: '/admin/parrots',
      permission: 'parrot.read',
      color: 'bg-green-600'
    },
    {
      title: 'Knowledge Base',
      description: 'Manage RAG files and content',
      icon: '📚',
      href: '/admin/rag',
      permission: 'rag.read',
      color: 'bg-purple-600'
    },
    {
      title: 'Phone Numbers',
      description: 'Manage Twilio phone numbers',
      icon: '📞',
      href: '/admin/phone-numbers',
      permission: 'phone.read',
      color: 'bg-yellow-600'
    },
    {
      title: 'Phone Queue',
      description: 'Review phone deletion requests',
      icon: '⏳',
      href: '/admin/phone-queue',
      permission: 'phone.read',
      color: 'bg-orange-600'
    },
    {
      title: 'Audit Logs',
      description: 'View system audit trails',
      icon: '📋',
      href: '/admin/audit',
      permission: 'audit.read',
      color: 'bg-red-600'
    },
    {
      title: 'Quick Templates',
      description: 'Manage quick action templates',
      icon: '📝',
      href: '/admin/quick-templates',
      permission: 'user.read', // Using existing permission for testing
      color: 'bg-indigo-600'
    },
    {
      title: 'Analytics',
      description: 'Platform usage statistics and insights',
      icon: '📊',
      href: '/admin/analytics',
      permission: 'user.read', // Using existing permission for testing
      color: 'bg-teal-600'
    },
    {
      title: 'System Health',
      description: 'Infrastructure monitoring and performance',
      icon: '🔧',
      href: '/admin/system-health',
      permission: 'user.read', // Using existing permission for testing
      color: 'bg-cyan-600'
    },
    {
      title: 'Settings',
      description: 'Admin settings and configuration',
      icon: '⚙️',
      href: '/admin/settings',
      permission: 'user.read', // Using existing permission for testing
      color: 'bg-gray-600'
    }
  ];

  const visibleCards = dashboardCards.filter(card => 
    !card.permission || hasPermission(card.permission)
  );



  return (
    <div>
      <div className="mb-8">
        <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Welcome to the ParrotMate administration panel. Select a section to manage.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-sm`}>
          <div className="flex items-center">
            <div className="text-2xl mr-4">👥</div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Users</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>-</p>
            </div>
          </div>
        </div>
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-sm`}>
          <div className="flex items-center">
            <div className="text-2xl mr-4">🦜</div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Active Parrots</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>-</p>
            </div>
          </div>
        </div>
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-sm`}>
          <div className="flex items-center">
            <div className="text-2xl mr-4">📞</div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Phone Numbers</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>-</p>
            </div>
          </div>
        </div>
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-sm`}>
          <div className="flex items-center">
            <div className="text-2xl mr-4">📊</div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>This Month</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>-</p>
            </div>
          </div>
        </div>
      </div>

      {/* Management Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleCards.map((card) => (
          <a
            key={card.title}
            href={card.href}
            className={`block ${theme === 'dark' ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} rounded-lg p-6 transition-colors shadow-sm`}
          >
            <div className="flex items-center mb-4">
              <div className={`p-3 rounded-lg ${card.color} mr-4`}>
                <span className="text-2xl">{card.icon}</span>
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{card.title}</h3>
              </div>
            </div>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{card.description}</p>
          </a>
        ))}
      </div>

      {/* System Status */}
      <div className={`mt-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-sm`}>
        <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>API Status: Operational</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Database: Operational</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>External APIs: Operational</span>
          </div>
        </div>
      </div>

      {/* Permissions Debug (Development Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`mt-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-sm`}>
          <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
            Debug: Current Permissions
          </h3>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <p className="mb-2">Active permissions:</p>
            <ul className="list-disc list-inside space-y-1">
              {permissions.map((permission) => (
                <li key={permission}>{permission}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOverview;
